import { defineStore } from 'pinia';
import { useNuxtApp } from "#app";
import { setMeta } from "~/utils/meta";
import { ArticleDetailType } from "~/types/article/ArticleDetailType";
import { contentAutoMapper } from "~/utils/mappers/content.mapper";

export const useArticleDetailStore = defineStore({
    id: 'articleDetail',
    state: (): ArticleDetailType => ({
        title: undefined,
        slug: undefined,
        date: undefined,
        dateModified: undefined,
        category: undefined,
        picture: undefined,
        content: undefined,
        error: undefined,
    }),
    getters: {
        getStructuredData(state: ArticleDetailType): object[] {
            return [
                {
                    "@context": "https://schema.org",
                    "@type": "NewsArticle",
                    "headline": state.title,
                    "image": [
                        this.$useNuxtApp.imgProxyResize(state.picture, 500),
                    ],
                    "datePublished": state.date,
                    "dateModified": state.dateModified,
                },
                {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Interesting Facts",
                            "item": this.$useRuntimeConfig.public.baseUrl + "/interesting-facts",
                        },
                        {
                            "@type": "ListItem",
                            "position": 2,
                            "name": state.category?.title,
                            "item": this.$useRuntimeConfig.public.baseUrl + state.category?.url,
                        },
                        {
                            "@type": "ListItem",
                            "position": 3,
                            "name": state.title,
                        }
                    ]
                }
            ];
        },
    },
    actions: {
        clear() {
            this.title = undefined;
            this.slug = undefined;
            this.date = undefined;
            this.category = undefined;
            this.picture = undefined;
            this.content = undefined;
            this.error = undefined;
        },

        async fetch(slug: string) {
            try {
                const response = await useNuxtApp().axios.get('/interesting-facts', {
                    params: {
                        'filters[slug][$eq]': slug,
                        fields: ['slug', 'title', 'publishedAt', 'updatedAt'],
                        'populate[interesting_facts_category][populate]': '*',
                        'populate[picture][populate]': '*',
                        'populate[content][populate]': '*',
                        'populate[seo][populate][0]': 'metaImage',
                    }
                });
                if (response?.data?.data === undefined || !response?.data?.data.length) {
                    this.error = createError({statusCode: 404, statusMessage: 'Page Not Found'});
                    return false;
                }

                const data = response?.data?.data[0];

                this.slug = data.attributes?.slug;
                this.title = data.attributes?.title;
                this.date = data.attributes?.publishedAt;
                this.dateModified = data.attributes?.updatedAt;
                this.content = data.attributes?.content.map(content => contentAutoMapper(content, this.$useRuntimeConfig));
                this.content = this.content.filter(content => content !== null);
                this.picture = this.$useRuntimeConfig.public.ssrMediaBaseUrl + data?.attributes?.picture?.data?.attributes?.url;

                if (data.attributes?.interesting_facts_category) {
                    this.category = {
                        slug: data.attributes?.interesting_facts_category?.data?.attributes?.slug,
                        title: data.attributes?.interesting_facts_category?.data?.attributes?.title,
                        url: `/interesting-facts/category-${data.attributes?.interesting_facts_category?.data?.attributes?.slug}`,
                    };
                }

                if (data.attributes?.seo) {
                    if (data.attributes.seo?.structuredData === undefined || data.attributes.seo?.structuredData === null) {
                        data.attributes.seo.structuredData = this.getStructuredData;
                    }

                    setMeta(data.attributes.seo, this.$useRuntimeConfig, this.$useRoute);
                }
            } catch (error) {
                console.log(error);
                this.error = createError({ statusCode: 500, statusMessage: 'Internal error' });
            }

            return true;
        },
    },
});
